import { useState } from 'react';
import { ChevronDown, ChevronUp, User, Palette, Building2, MapPin, Share2, Briefcase, BookOpen, Image, Sparkles, Check, AlertCircle } from 'lucide-react';
import type { OnboardingStepsData, BusinessProfile } from '../types/api';
import { Card } from './ui/Card';

interface OnboardingDetailsProps {
  data: OnboardingStepsData;
}

// Step configuration with labels and icons
const STEP_CONFIG = [
  { key: 'step1', label: 'Brand Personality', icon: User },
  { key: 'step3', label: 'Brand Assets', icon: Palette },
  { key: 'step2', label: 'Visual Style', icon: Palette },
  { key: 'step4', label: 'Business Details', icon: Building2 },
  { key: 'step5', label: 'Location & Hours', icon: MapPin },
  { key: 'step6', label: 'Social Media', icon: Share2 },
  { key: 'step7', label: 'Industry', icon: Briefcase },
  { key: 'step8', label: 'Your Story', icon: BookOpen },
  { key: 'step9', label: 'Gallery', icon: Image },
] as const;

export function OnboardingDetails({ data }: OnboardingDetailsProps) {
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set(['businessProfile']));

  const toggleStep = (stepKey: string) => {
    setExpandedSteps(prev => {
      const next = new Set(prev);
      if (next.has(stepKey)) {
        next.delete(stepKey);
      } else {
        next.add(stepKey);
      }
      return next;
    });
  };

  const hasData = (stepData: unknown): boolean => {
    if (!stepData) return false;
    if (typeof stepData === 'object') {
      return Object.values(stepData).some(v => v !== null && v !== undefined && v !== '');
    }
    return true;
  };

  return (
    <div className="space-y-4">
      {/* AI Business Profile (highlighted at top) */}
      {data.businessProfile && (
        <BusinessProfileCard
          profile={data.businessProfile}
          clarificationCount={data.profileClarificationCount}
          escalated={data.profileEscalated}
          expanded={expandedSteps.has('businessProfile')}
          onToggle={() => toggleStep('businessProfile')}
        />
      )}

      {/* All Steps */}
      <Card>
        <h3 className="font-semibold text-gray-900 mb-4">Onboarding Responses</h3>
        <div className="space-y-2">
          {STEP_CONFIG.map(({ key, label, icon: Icon }) => {
            const stepData = data[key as keyof OnboardingStepsData];
            const expanded = expandedSteps.has(key);
            const filled = hasData(stepData);

            return (
              <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleStep(key)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-4 h-4 text-gray-500" />
                    <span className="font-medium text-gray-900">{label}</span>
                    {filled ? (
                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                        Completed
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">
                        Empty
                      </span>
                    )}
                  </div>
                  {expanded ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </button>

                {expanded && stepData && (
                  <div className="px-4 py-3 bg-white border-t border-gray-200">
                    <StepContent stepKey={key} data={stepData} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// Business Profile Card component
function BusinessProfileCard({
  profile,
  clarificationCount,
  escalated,
  expanded,
  onToggle,
}: {
  profile: BusinessProfile;
  clarificationCount: number;
  escalated: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <Card className="border-primary-200 bg-primary-50/30">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Sparkles className="w-5 h-5 text-primary-600" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900">AI Business Profile</h3>
            <p className="text-sm text-gray-500">
              {profile.confirmedAt ? 'Confirmed by customer' : 'Generated summary'}
            </p>
          </div>
          {profile.confirmedAt && (
            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full flex items-center gap-1">
              <Check className="w-3 h-3" />
              Confirmed
            </span>
          )}
          {escalated && (
            <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Escalated
            </span>
          )}
          {clarificationCount > 0 && (
            <span className="text-xs text-gray-500">
              {clarificationCount} clarification{clarificationCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-primary-200 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Industry</p>
              <p className="text-gray-900">
                {profile.industry.primary}
                {profile.industry.specific && ` - ${profile.industry.specific}`}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Tone</p>
              <p className="text-gray-900 capitalize">{profile.tone}</p>
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase mb-1">What They Do</p>
            <p className="text-gray-900">{profile.coreSummary}</p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase mb-1">Atmosphere/Vibe</p>
            <p className="text-gray-900">{profile.atmosphere}</p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase mb-1">Ideal Client</p>
            <p className="text-gray-900">{profile.idealClient}</p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase mb-1">What Sets Them Apart</p>
            <p className="text-gray-900">{profile.uniqueValue}</p>
          </div>

          {profile.services && profile.services.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Key Services</p>
              <div className="flex flex-wrap gap-2">
                {profile.services.map((service, i) => (
                  <span key={i} className="text-sm px-2 py-1 bg-gray-100 text-gray-700 rounded">
                    {service}
                  </span>
                ))}
              </div>
            </div>
          )}

          {profile.imageKeywords && profile.imageKeywords.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Image Keywords</p>
              <div className="flex flex-wrap gap-2">
                {profile.imageKeywords.map((keyword, i) => (
                  <span key={i} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded">
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// Step content renderer
function StepContent({ stepKey, data }: { stepKey: string; data: unknown }) {
  if (!data || typeof data !== 'object') {
    return <p className="text-gray-500 text-sm">No data</p>;
  }

  const stepData = data as Record<string, unknown>;

  // Custom renderers for specific steps
  switch (stepKey) {
    case 'step1':
      return (
        <DataGrid data={{
          'Personality': stepData.personality,
        }} />
      );

    case 'step2':
      return (
        <DataGrid data={{
          'Recipe/Style': stepData.recipe,
        }} />
      );

    case 'step3':
      return (
        <div className="space-y-3">
          {typeof stepData.logo_url === 'string' && stepData.logo_url ? (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">Logo</p>
              <img
                src={stepData.logo_url}
                alt="Business logo"
                className="h-16 object-contain bg-gray-100 rounded p-2"
              />
            </div>
          ) : null}
          <div className="flex gap-4">
            {typeof stepData.primary_color === 'string' && stepData.primary_color ? (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Primary Color</p>
                <div className="flex items-center gap-2">
                  <div
                    className="w-8 h-8 rounded border"
                    style={{ backgroundColor: stepData.primary_color }}
                  />
                  <span className="text-sm text-gray-700">{stepData.primary_color}</span>
                </div>
              </div>
            ) : null}
            {typeof stepData.secondary_color === 'string' && stepData.secondary_color ? (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Secondary Color</p>
                <div className="flex items-center gap-2">
                  <div
                    className="w-8 h-8 rounded border"
                    style={{ backgroundColor: stepData.secondary_color }}
                  />
                  <span className="text-sm text-gray-700">{stepData.secondary_color}</span>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      );

    case 'step4':
      return (
        <DataGrid data={{
          'Tagline': stepData.tagline,
          'Phone': stepData.phone,
          'Email': stepData.email,
          'Services': Array.isArray(stepData.services_list)
            ? (stepData.services_list as string[]).join(', ')
            : stepData.services_text,
          'Additional Notes': stepData.additional_notes,
        }} />
      );

    case 'step5':
      return (
        <DataGrid data={{
          'Service Area Type': stepData.service_area_type,
          'Address': stepData.address,
          'City': stepData.city,
          'State': stepData.state,
          'Zip': stepData.zip,
          'Service Radius': stepData.service_radius ? `${stepData.service_radius} miles` : undefined,
          'Service Regions': Array.isArray(stepData.service_regions)
            ? (stepData.service_regions as string[]).join(', ')
            : undefined,
        }} />
      );

    case 'step6':
      return (
        <DataGrid data={{
          'Facebook': stepData.facebook,
          'Instagram': stepData.instagram,
          'Twitter/X': stepData.twitter,
          'LinkedIn': stepData.linkedin,
          'YouTube': stepData.youtube,
          'TikTok': stepData.tiktok,
          'Yelp': stepData.yelp,
          'Google Reviews': stepData.google_reviews,
        }} />
      );

    case 'step7':
      return (
        <div className="space-y-3">
          <DataGrid data={{
            'Category': stepData.industry_category,
            'Type': stepData.industry_type,
            'Other (specified)': stepData.industry_type_other,
          }} />
          {stepData.industry_specific && typeof stepData.industry_specific === 'object' ? (
            <div className="pt-2 border-t border-gray-200">
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">Industry-Specific Details</p>
              <DataGrid data={stepData.industry_specific as Record<string, unknown>} />
            </div>
          ) : null}
        </div>
      );

    case 'step8':
      return (
        <DataGrid data={{
          'Story Type': stepData.story_type,
          'Years in Business': stepData.years_in_business,
          'Origin Story': stepData.origin_story,
          'Mission': stepData.mission,
          'Team Size': stepData.team_size,
          'Team Highlight': stepData.team_highlight,
          'Certifications': Array.isArray(stepData.certifications)
            ? (stepData.certifications as string[]).join(', ')
            : undefined,
          'Awards': Array.isArray(stepData.awards)
            ? (stepData.awards as string[]).join(', ')
            : undefined,
          'What Makes You Different': stepData.why_different,
          'Customer Promise': stepData.customer_promise,
          'Fun Fact': stepData.fun_fact,
        }} />
      );

    case 'step9':
      return (
        <div className="space-y-3">
          <DataGrid data={{
            'Image Preference': stepData.image_preference,
          }} />
          <div className="grid grid-cols-2 gap-4">
            {typeof stepData.hero_image_url === 'string' && stepData.hero_image_url ? (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Hero Image</p>
                <img
                  src={stepData.hero_image_url}
                  alt="Hero"
                  className="w-full h-24 object-cover rounded"
                />
              </div>
            ) : null}
            {[1, 2, 3, 4].map(n => {
              const key = `gallery_image_${n}_url`;
              const url = stepData[key];
              if (typeof url !== 'string' || !url) return null;
              return (
                <div key={n}>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Gallery {n}</p>
                  <img src={url} alt={`Gallery ${n}`} className="w-full h-24 object-cover rounded" />
                </div>
              );
            })}
          </div>
        </div>
      );

    default:
      // Generic renderer for any step data
      return <DataGrid data={stepData} />;
  }
}

// Format value for display - handles unknown types safely
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(v => String(v)).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// Generic data grid renderer
function DataGrid({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data).filter(([, v]) =>
    v !== null && v !== undefined && v !== ''
  );

  if (entries.length === 0) {
    return <p className="text-gray-500 text-sm">No data provided</p>;
  }

  return (
    <dl className="space-y-2">
      {entries.map(([key, value]) => (
        <div key={key}>
          <dt className="text-xs font-medium text-gray-500 uppercase">{formatLabel(key)}</dt>
          <dd className="text-sm text-gray-900 mt-0.5">
            {formatValue(value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

// Format snake_case or camelCase to Title Case
function formatLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^\w/, c => c.toUpperCase())
    .trim();
}
